import Layout from "../components/site/Layout";
import { PageHeader, SectionLabel, SectionHeading } from "../components/site/Primitives";
import { ArrowUpRight } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { FIRM } from "../lib/firm";
import { trackEvent } from "../lib/analytics";

const ELEMENTS = [
  {
    tag: "E/01",
    title: "The Structure",
    body:
      "An IRA-owned LLC is established with the client as manager and the IRA as minority member — a structure the IRS expressly acknowledged in its 1996 Swanson guidance.",
  },
  {
    tag: "E/02",
    title: "The Operating Agreement",
    body:
      "Specific provisions in a professionally drafted operating agreement — transfer restrictions, redemption limitations, and control constraints — create the conditions that support a valuation discount under standard appraisal methodology.",
  },
  {
    tag: "E/03",
    title: "The Appraisal",
    body:
      "An independent, qualified business appraiser determines the fair market value of the LLC interest at the time of conversion. The discount is not assumed — it is documented, defensible, and specific to the structure.",
  },
];

const REFS = [
  {
    tag: "REF / 01",
    src: "IRS (1996)",
    desc: "Swanson v. Commissioner — IRS acknowledgment that an IRA may own an LLC interest",
  },
  {
    tag: "REF / 02",
    src: "Rev. Rul. 59-60",
    desc: "IRS guidance establishing that FMV must consider marketability and degree of control",
  },
  {
    tag: "REF / 03",
    src: "IRS Letter EP:R:9",
    desc: "Confirms that the FMV standard applies to IRA-owned LLC interests at conversion",
  },
  {
    tag: "REF / 04",
    src: "Pierre v. Commissioner; Estate of Gallagher v. Commissioner",
    desc: "Tax Court upholding combined DLOC/DLOM discounts of 30–45%",
  },
  {
    tag: "REF / 05",
    src: "IRS DLOM Job Aid (2009) / IRM 4.48.4",
    desc: "IRS examiner guidance confirming DLOM as accepted methodology",
  },
];

const FITS = [
  { factor: "IRA / 401(k) Balance", strong: "$1,000,000 or more", notFit: "Under $500,000" },
  { factor: "Conversion Intent", strong: "Planning a full or partial Roth conversion", notFit: "No conversion planned" },
  { factor: "Tax Bracket", strong: "32% or higher (current or projected)", notFit: "22% or lower" },
  { factor: "Time Horizon", strong: "Multi-year conversion plan during pre-RMD years", notFit: "No urgency to convert" },
  { factor: "Structure Tolerance", strong: "Willing to engage counsel, appraiser, and implementation team", notFit: "Seeking a simple transaction" },
];

const ROLE = [
  "Retirement Tax Risk Analysis identifying total conversion opportunity and projected tax savings",
  "Strategy recommendation and implementation roadmap",
  "Coordination with tax counsel for LLC formation and operating agreement drafting",
  "Referral to qualified independent appraiser for LLC interest valuation at conversion",
  "Integration with your existing CPA and financial advisor throughout the process",
  "Ongoing oversight through retirement and future legislative shifts",
];

function ProblemSection() {
  return (
    <section className="py-24 lg:py-32 bg-cream-alt" data-testid="acs-problem">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <SectionLabel index="01" label="The Problem" />
        <SectionHeading italicTail="not face value.">
          The conversion tax is calculated on fair market value —
        </SectionHeading>
        <div className="mt-10 grid lg:grid-cols-2 gap-12 lg:gap-20">
          <div className="space-y-6 text-charcoal-teal leading-relaxed">
            <p>
              Most advisors treat the two as identical. Under the right
              structure, they are not. When you convert a traditional IRA to
              a Roth, the IRS taxes the converted amount as ordinary income
              — based on the fair market value of the assets being
              converted.
            </p>
            <p>
              For a standard IRA holding publicly traded securities, fair
              market value and account balance are the same thing. But for
              an IRA that holds an interest in a properly structured LLC,
              the analysis is different. The fair market value of an LLC
              interest — the thing actually being converted — is determined
              by appraisal.
            </p>
            <p>
              A professionally appraised LLC interest, structured with
              appropriate operating agreement provisions, may appraise at a
              meaningful discount to the LLC&rsquo;s underlying asset value. That
              discount reduces the amount subject to ordinary income tax at
              conversion.
            </p>
          </div>
          <aside className="bg-cream border-l-2 border-accent-gold p-8 self-start">
            <div className="text-[10px] tracking-overline uppercase font-semibold text-accent-gold">
              The Result
            </div>
            <p className="mt-4 font-serif text-deep-teal text-2xl leading-snug">
              A lower tax liability on the same economic transfer — without
              surrendering control of your investments, without locking up
              capital in illiquid assets, and without deviation from IRS
              guidance.
            </p>
            <p className="mt-6 text-sm text-charcoal-teal leading-relaxed">
              This is not a tax shelter. It is the application of a standard
              valuation principle — one the IRS uses in its own published
              guidance — to an asset class where it is legally applicable
              and professionally documented.
            </p>
          </aside>
        </div>
      </div>
    </section>
  );
}

function HowItWorksSection() {
  return (
    <section className="py-24 lg:py-32 bg-cream" data-testid="acs-how">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <SectionLabel index="02" label="How It Works" />
        <SectionHeading italicTail="legally defensible.">
          Three elements make the discount
        </SectionHeading>

        <div className="mt-14 grid lg:grid-cols-3 gap-px bg-sage/30 border border-sage/30">
          {ELEMENTS.map((e) => (
            <div key={e.tag} className="bg-cream p-8 lg:p-10">
              <div className="text-[10px] tracking-overline uppercase font-semibold text-accent-gold mb-6">
                {e.tag}
              </div>
              <h3 className="font-serif text-deep-teal text-2xl leading-snug">
                {e.title}
              </h3>
              <p className="mt-4 text-charcoal-teal text-sm leading-relaxed">
                {e.body}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-14 bg-deep-teal text-cream p-10 lg:p-14">
          <div className="text-[10px] tracking-overline uppercase font-semibold text-accent-gold mb-4">
            Illustrative Outcome
          </div>
          <p className="font-serif text-2xl lg:text-3xl leading-snug">
            A client converting $2,000,000 in traditional IRA assets through
            a properly structured IRA-LLC may convert at an appraised fair
            market value of $1.3M – $1.5M — reducing taxable income by
            $500,000–$700,000.
          </p>
          <p className="mt-6 text-sm text-cream/80 leading-relaxed">
            Actual discount ranges are determined by independent appraisal
            and vary by structure and operating agreement provisions.
          </p>
        </div>
      </div>
    </section>
  );
}

function LegalFoundationSection() {
  return (
    <section className="py-24 lg:py-32 bg-cream-alt" data-testid="acs-legal">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <SectionLabel index="03" label="Legal Foundation" />
        <SectionHeading italicTail="established case law.">
          Built on published IRS guidance and
        </SectionHeading>
        <p className="mt-6 text-charcoal-teal max-w-3xl leading-relaxed">
          This strategy is not novel or experimental. Its legal basis has
          been developed over three decades of IRS guidance and Tax Court
          precedent.
        </p>

        <div className="mt-12 border-t border-sage-soft">
          {REFS.map((r) => (
            <div
              key={r.tag}
              className="grid lg:grid-cols-12 gap-6 py-7 border-b border-sage-soft items-start"
            >
              <div className="lg:col-span-2 text-[10px] tracking-overline uppercase font-semibold text-accent-gold">
                {r.tag}
              </div>
              <div className="lg:col-span-4 font-serif text-deep-teal text-xl">
                {r.src}
              </div>
              <div className="lg:col-span-6 text-charcoal-teal text-sm leading-relaxed">
                {r.desc}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FitSection() {
  return (
    <section className="py-24 lg:py-32 bg-cream" data-testid="acs-fit">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <SectionLabel index="04" label="Who This Is For" />
        <SectionHeading italicTail="specific client profile.">
          Appropriate for a narrow,
        </SectionHeading>

        <div className="mt-14 overflow-hidden border border-sage/40">
          <div className="grid grid-cols-12 bg-deep-teal text-cream text-[10px] tracking-overline uppercase font-semibold">
            <div className="col-span-4 p-5">Factor</div>
            <div className="col-span-4 p-5">Strong Candidate</div>
            <div className="col-span-4 p-5">Not a Fit</div>
          </div>
          {FITS.map((f, i) => (
            <div
              key={f.factor}
              className={`grid grid-cols-12 ${i % 2 ? "bg-cream-alt" : "bg-cream"}`}
            >
              <div className="col-span-4 p-5 text-sm font-semibold text-deep-teal">
                {f.factor}
              </div>
              <div className="col-span-4 p-5 text-sm text-charcoal-teal">
                {f.strong}
              </div>
              <div className="col-span-4 p-5 text-sm text-charcoal-teal/70 italic">
                {f.notFit}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function RoleSection() {
  return (
    <section className="py-24 lg:py-32 bg-cream-alt" data-testid="acs-role">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <SectionLabel index="05" label="Our Role" />
        <SectionHeading italicTail="the full implementation.">
          RTC coordinates
        </SectionHeading>
        <p className="mt-6 text-charcoal-teal max-w-3xl leading-relaxed">
          This strategy requires a CPA or accountant, a tax attorney, an
          estate planning specialist, a business appraiser, and a retirement
          tax strategist working from a unified plan. Our family office
          model provides that coordination as a single engagement.
        </p>

        <ol className="mt-12 grid sm:grid-cols-2 gap-px bg-sage/30 border border-sage/30">
          {ROLE.map((step, i) => (
            <li key={step} className="bg-cream p-7 lg:p-8 flex gap-5">
              <span className="text-[10px] tracking-overline uppercase font-semibold text-accent-gold pt-1">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="text-deep-teal leading-relaxed">{step}</span>
            </li>
          ))}
        </ol>

        <div className="mt-14 flex flex-wrap items-center gap-6">
          <Link
            to="/strategy-comparison-gate"
            data-testid="acs-strategy-cta"
            className="group inline-flex items-center gap-3 px-7 py-4 bg-accent-gold !text-white text-xs tracking-overline uppercase font-medium hover:bg-accent-gold-hover transition-all"
          >
            Download the Strategy Comparison
            <ArrowUpRight
              size={16}
              weight="light"
              className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"
            />
          </Link>
          <a
            href={FIRM.calendly15}
            target="_blank"
            rel="noreferrer"
            onClick={() =>
              trackEvent("calendly_click_15min", {
                meta: { source: "advanced_strategies" },
              })
            }
            className="inline-flex items-center gap-2 text-sm text-deep-teal editorial-link"
          >
            Schedule a Retirement Tax Risk Analysis
            <ArrowUpRight size={16} weight="light" />
          </a>
        </div>

        <p className="mt-14 text-xs italic text-charcoal-teal/70 leading-relaxed max-w-4xl">
          Discount ranges are illustrative and subject to independent
          appraisal on a case-by-case basis. This page is for informational
          purposes only and does not constitute tax, legal, or investment
          advice. Strategy suitability depends on individual circumstances.
          Retirement Tax Consultants, LLC coordinates with qualified tax
          counsel and independent appraisers; RTC does not provide legal
          services or appraisal services directly.
        </p>
      </div>
    </section>
  );
}

export default function AdvancedStrategiesPage() {
  return (
    <Layout pageName="advanced-conversion-strategies">
      <PageHeader
        eyebrow="Advanced Conversion Strategies"
        title="Conventional Roth conversions may be"
        italicTail="overpricing your tax liability."
        intro="For clients with $1 million or more in traditional IRA or 401(k) balances, the standard conversion calculation often overstates what you actually owe — because it ignores how the IRS defines fair market value."
      />
      <ProblemSection />
      <HowItWorksSection />
      <LegalFoundationSection />
      <FitSection />
      <RoleSection />
    </Layout>
  );
}
